import React from "react";
import {
  MilestoneItem,
  getMilestoneMarkerPositions,
  MILESTONE_VIEW_WIDTH,
  MILESTONE_VIEW_HEIGHT,
  MILESTONE_TRACK_Y,
  MILESTONE_TRACK_X_START,
  MILESTONE_TRACK_X_END,
  MILESTONE_TRACK_WIDTH,
  MILESTONE_TRACK_STROKE,
  MILESTONE_LABEL_Y,
  MILESTONE_SUMMARY_Y,
  MILESTONE_MAX_RENDERED,
  MILESTONE_LABEL_ROTATE_THRESHOLD,
  SR_ONLY_STYLE,
} from "./gaugeUtils";

export interface MilestoneRendererProps {
  // Data
  milestones: MilestoneItem[];

  // Colors (resolved — no CSS variables)
  completedColor: string;
  trackColor: string;

  // Marker appearance
  markerStyle: 'circle' | 'diamond' | 'square';
  markerSize: number;

  // Label display
  showLabels: boolean;

  // Summary
  label: string;
  showSummary: boolean;

  // Font
  labelFontSize: number;
  summaryFontSize: number;
}

function renderMarkerShape(
  shape: 'circle' | 'diamond' | 'square',
  cx: number,
  cy: number,
  size: number,
  completed: boolean,
  completedColor: string,
  trackColor: string,
  index: number,
  label: string,
): React.ReactElement {
  const half = size / 2 - 2;
  const fillColor = completed ? completedColor : 'white';
  const strokeColor = completed ? completedColor : trackColor;
  const tooltip = `${label}: ${completed ? 'complete' : 'pending'}`;

  if (shape === 'diamond') {
    const r = size / 2 - 2;
    return (
      <polygon
        key={`marker-${index}`}
        points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={2}
        data-testid={`gauge-milestone-marker-${index}`}
      >
        <title>{tooltip}</title>
      </polygon>
    );
  }

  if (shape === 'square') {
    return (
      <rect
        key={`marker-${index}`}
        x={cx - half}
        y={cy - half}
        width={half * 2}
        height={half * 2}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={2}
        data-testid={`gauge-milestone-marker-${index}`}
      >
        <title>{tooltip}</title>
      </rect>
    );
  }

  // circle (default)
  return (
    <circle
      key={`marker-${index}`}
      cx={cx}
      cy={cy}
      r={half}
      fill={fillColor}
      stroke={strokeColor}
      strokeWidth={2}
      data-testid={`gauge-milestone-marker-${index}`}
    >
      <title>{tooltip}</title>
    </circle>
  );
}

export const MilestoneRenderer: React.FC<MilestoneRendererProps> = ({
  milestones,
  completedColor,
  trackColor,
  markerStyle,
  markerSize,
  showLabels,
  label,
  showSummary,
  labelFontSize,
  summaryFontSize,
}) => {
  // Filter out malformed entries (runtime safety for cast data)
  const safeMilestones = milestones.filter(
    m => m && typeof m.label === 'string' && typeof m.completed === 'boolean'
  );

  // Clamp to max renderable
  const visibleMilestones = safeMilestones.slice(0, MILESTONE_MAX_RENDERED);
  const overflowCount = safeMilestones.length - visibleMilestones.length;
  const total = visibleMilestones.length;
  const totalCompletedCount = safeMilestones.filter(m => m.completed).length;

  // X positions for markers
  const positions = getMilestoneMarkerPositions(total);

  // Track fill: from TRACK_X_START to X of last completed marker
  let lastCompletedIndex = -1;
  for (let i = total - 1; i >= 0; i--) {
    if (visibleMilestones[i].completed) {
      lastCompletedIndex = i;
      break;
    }
  }
  const filledEndX = lastCompletedIndex >= 0
    ? positions[lastCompletedIndex]
    : MILESTONE_TRACK_X_START;
  const hasAnyCompleted = lastCompletedIndex >= 0;

  // Label rotation: rotate 45° when spacing is tight
  const spacing = total >= 2 ? positions[1] - positions[0] : MILESTONE_TRACK_WIDTH;
  const rotateLabels = spacing < MILESTONE_LABEL_ROTATE_THRESHOLD;

  const ariaLabel = label
    ? `${label}: ${totalCompletedCount} of ${safeMilestones.length} milestones complete`
    : `${totalCompletedCount} of ${safeMilestones.length} milestones complete`;

  return (
    <div
      data-testid="gauge-milestone-renderer"
      role="list"
      aria-label={ariaLabel}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* Visually hidden screen-reader list items */}
      <div style={SR_ONLY_STYLE}>
        {safeMilestones.map((m, i) => (
          <span
            key={m.id}
            role="listitem"
            data-testid={`gauge-milestone-sr-item-${i}`}
          >
            {m.label}: {m.completed ? 'complete' : 'pending'}
          </span>
        ))}
        {overflowCount > 0 && (
          <span role="listitem">
            {overflowCount} more milestones not shown
          </span>
        )}
      </div>

      {total === 0 ? (
        <div
          data-testid="gauge-milestone-empty"
          style={{ color: trackColor, fontSize: '12px', textAlign: 'center' }}
        >
          No milestones defined
        </div>
      ) : (
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${MILESTONE_VIEW_WIDTH} ${MILESTONE_VIEW_HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
          data-testid="gauge-milestone-svg"
        >
          {/* Track background (full length) */}
          <line
            x1={MILESTONE_TRACK_X_START}
            y1={MILESTONE_TRACK_Y}
            x2={MILESTONE_TRACK_X_END}
            y2={MILESTONE_TRACK_Y}
            stroke={trackColor}
            strokeWidth={MILESTONE_TRACK_STROKE}
            strokeLinecap="round"
            strokeOpacity={0.3}
            data-testid="gauge-milestone-track"
          />

          {/* Track filled portion (start to last completed) */}
          {hasAnyCompleted && (
            <line
              x1={MILESTONE_TRACK_X_START}
              y1={MILESTONE_TRACK_Y}
              x2={filledEndX}
              y2={MILESTONE_TRACK_Y}
              stroke={completedColor}
              strokeWidth={MILESTONE_TRACK_STROKE}
              strokeLinecap="round"
              data-testid="gauge-milestone-track-fill"
            />
          )}

          {/* Markers */}
          {visibleMilestones.map((m, i) =>
            renderMarkerShape(
              markerStyle,
              positions[i],
              MILESTONE_TRACK_Y,
              markerSize,
              m.completed,
              completedColor,
              trackColor,
              i,
              m.label,
            )
          )}

          {/* Labels below markers */}
          {showLabels && visibleMilestones.map((m, i) => {
            const lx = positions[i];
            const truncated = m.label.length > 8
              ? m.label.substring(0, 7) + '\u2026'
              : m.label;
            return (
              <text
                key={`label-${i}`}
                x={lx}
                y={MILESTONE_LABEL_Y}
                textAnchor={rotateLabels ? 'end' : 'middle'}
                fontSize={labelFontSize}
                fill={trackColor}
                transform={rotateLabels ? `rotate(-45, ${lx}, ${MILESTONE_LABEL_Y})` : undefined}
                data-testid={`gauge-milestone-label-${i}`}
              >
                {m.label.length > 8 && <title>{m.label}</title>}
                {truncated}
              </text>
            );
          })}

          {/* Overflow indicator */}
          {overflowCount > 0 && (
            <text
              x={MILESTONE_TRACK_X_END + 4}
              y={MILESTONE_TRACK_Y + 1}
              dominantBaseline="central"
              fontSize={10}
              fill={trackColor}
              data-testid="gauge-milestone-overflow"
            >
              +{overflowCount}
            </text>
          )}

          {/* Summary text */}
          {showSummary && (
            <text
              x={MILESTONE_VIEW_WIDTH / 2}
              y={MILESTONE_SUMMARY_Y}
              textAnchor="middle"
              fontSize={summaryFontSize}
              fill={trackColor}
              data-testid="gauge-milestone-summary"
            >
              {totalCompletedCount} of {safeMilestones.length} complete
            </text>
          )}
        </svg>
      )}
    </div>
  );
};
